import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Cache the fetch so every mount doesn't hit the API — levels change rarely.
let _cache = null;
let _promise = null;

/** Fallback used when the API hasn't answered yet — keeps the UI responsive. */
export const CUSTOMER_LEVELS_FALLBACK = {
  Standard: 0,
  "Level 1": 1,
  "Level 2": 2.5,
  "Level 3": 5,
};

async function fetchLevels() {
  if (_cache) return _cache;
  if (!_promise) {
    _promise = axios
      .get(`${API}/customer-levels`)
      .then((res) => {
        const list = res.data?.levels || [];
        const map = {};
        list.forEach((l) => { map[l.name] = Number(l.discount) || 0; });
        _cache = { list, map };
        return _cache;
      })
      .catch(() => {
        _cache = {
          list: Object.entries(CUSTOMER_LEVELS_FALLBACK).map(([name, discount]) => ({
            name,
            discount,
            description: name === "Standard" ? "No automatic discount" : `${discount}% automatic discount`,
          })),
          map: { ...CUSTOMER_LEVELS_FALLBACK },
        };
        return _cache;
      })
      .finally(() => { _promise = null; });
  }
  return _promise;
}

/** Invalidate the cache — call after saving new level rates. */
export function invalidateCustomerLevelsCache() {
  _cache = null;
}

/** Hook: returns { list, map, loading } and re-fetches on demand. */
export function useCustomerLevels() {
  const [state, setState] = useState(() => _cache || {
    list: [],
    map: { ...CUSTOMER_LEVELS_FALLBACK },
    loading: !_cache,
  });

  useEffect(() => {
    let alive = true;
    fetchLevels().then((data) => {
      if (alive) setState({ ...data, loading: false });
    });
    return () => { alive = false; };
  }, []);

  return state;
}
